import  { useState } from 'react';
import { OmnichannelMessage } from '../../types';
import { useGetEmailsByUserId } from '../../services/query/messaging/email';
import { useGetSMSByUserId } from '../../services/query/messaging/sms';
import { Phone, MessageSquare, Mail,  Bot, ChevronRight, X, Download } from 'lucide-react';
import { useGetUserCallsById } from '../../services/query/calls/callsQueries';
import { useGetConversationDetails } from '../../services/query/calls/conversations';
import type { ConversationDetails } from '../../services/api/calls/conversationsApi';
import { UserCall } from '../../services/api/calls/callsApi';

export default function CommunicationHistory({
  patientId,
  messages,
}: {
  patientId?: number;
  messages?: OmnichannelMessage[];
}) {
  const [selectedCall, setSelectedCall] = useState<UserCall | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<OmnichannelMessage | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'call' | 'sms' | 'email'>('all');
  // fetch messages by user when patientId is provided
  const { data: emailsResult } = useGetEmailsByUserId(patientId);
  const { data: smsResult } = useGetSMSByUserId(patientId);
  // fetch calls by user when patientId is provided
  const { data: userCallsResult } = useGetUserCallsById(patientId);

  const fetchedMessages: OmnichannelMessage[] = [
    ...(emailsResult?.data ?? []),
    ...(smsResult?.data ?? []),
  ];

  // merge fetched messages with any messages passed via props and dedupe by id
  const mergedMap = new Map<string, OmnichannelMessage>();
  // prefer messages passed in props over fetched duplicates
  for (const m of fetchedMessages) mergedMap.set(m.id, m);
  for (const m of messages ?? []) mergedMap.set(m.id, m);
  const messagesList = Array.from(mergedMap.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  // use fetched calls by user id
  const mergedCalls: UserCall[] = (userCallsResult?.data ?? []).slice().sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());

  const smsMessages = messagesList.filter(m => m.channel === 'sms');
  const emailMessages = messagesList.filter(m => m.channel === 'email');

  const allCommunications = [
    ...mergedCalls.map(c => ({ type: 'call' as const, data: c, timestamp: (c as UserCall).start_time ?? (c as UserCall).start_time })),
    ...messagesList.map(m => ({ type: m.channel as 'sms' | 'email', data: m, timestamp: m.timestamp }))
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const filteredCommunications = filterType === 'all'
    ? allCommunications
    : allCommunications.filter(c =>
        filterType === 'call' ? c.type === 'call' :
        filterType === 'sms' ? c.type === 'sms' :
        filterType === 'email' ? c.type === 'email' : true
      );

  // fetch conversation details for the selected call
  const { data: conversationDetails, isLoading: isConvLoading } = useGetConversationDetails(selectedCall?.id as number | undefined);

  const downloadTranscript = () => {
    const details: ConversationDetails | undefined = conversationDetails;
    const msgs = details?.conversation ?? [];
    const transcript = msgs.map((msg: ConversationDetails['conversation'][0]) =>
      `[${new Date(msg.created_at ?? msg.timestamp).toLocaleTimeString()}] ${((msg.speaker || msg.type) as string).toUpperCase()}: ${msg.text ?? ''}`
    ).join('\n');

    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-transcript-${selectedCall?.id ?? 'unknown'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Communication History</h3>
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'all'
                ? 'bg-white text-green-700 shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All ({allCommunications.length})
          </button>
          <button
            onClick={() => setFilterType('call')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'call'
                ? 'bg-white text-green-700 shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calls ({mergedCalls.length})
          </button>
          <button
            onClick={() => setFilterType('sms')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'sms'
                ? 'bg-white text-green-700 shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            SMS ({smsMessages.length})
          </button>
          <button
            onClick={() => setFilterType('email')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filterType === 'email'
                ? 'bg-white text-green-700 shadow'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Email ({emailMessages.length})
          </button>
        </div>
      </div>

      {/* Communication List */}
      <div className="space-y-3">
        {filteredCommunications.map((comm, index) => (
          <div key={`${comm.type}-${index}`}>
                {comm.type === 'call' && (
                  // API returns `UserCall` objects (start_time etc). If the item looks like the API shape, render APICallItem and wire click to open details.
                  (comm.data as UserCall).start_time ? (
                    <APICallItem call={comm.data as UserCall} onClick={() => setSelectedCall(comm.data as UserCall)} />
                  ) : (
                    <CallItem
                      call={comm.data as UserCall}
                      onClick={() => setSelectedCall(comm.data as UserCall)}
                    />
                  )
                )}
            {(comm.type === 'sms' || comm.type === 'email') && (
              <MessageItem
                message={comm.data as OmnichannelMessage}
                onClick={() => setSelectedMessage(comm.data as OmnichannelMessage)}
              />
            )}
          </div>
        ))}

        {filteredCommunications.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">No communications found</p>
          </div>
        )}
      </div>

      {/* Call Detail Modal */}
      {selectedCall && (
        <CallDetailModal
          call={selectedCall}
          conversationDetails={conversationDetails}
          isLoading={isConvLoading}
          onClose={() => setSelectedCall(null)}
          onDownload={downloadTranscript}
        />
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageDetailModal message={selectedMessage} onClose={() => setSelectedMessage(null)} />
      )}
    </div>
  );
}

function CallItem({ call, onClick }: { call: UserCall; onClick: () => void }) {
  // const getUrgencyColor = (urgency: string) => {
  //   switch (urgency) {
  //     case 'emergency': return 'bg-red-100 text-red-800';
  //     case 'high': return 'bg-orange-100 text-orange-800';
  //     case 'medium': return 'bg-yellow-100 text-yellow-800';
  //     case 'low': return 'bg-green-100 text-green-800';
  //     default: return 'bg-gray-100 text-gray-800';
  //   }
  // };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all text-left"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          {/* <div className={`p-3 rounded-full ${call.type === 'inbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
            <Phone className={`w-5 h-5 ${call.type === 'inbound' ? 'text-blue-600' : 'text-green-600'}`} />
          </div> */}
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              {/* <span className="font-semibold text-gray-900 capitalize">{call.purpose} Call</span> */}
              {/* <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyColor(call.urgencyLevel)}`}>
                {call.urgencyLevel}
              </span>
              <span className={`text-xs font-medium ${call.type === 'inbound' ? 'text-blue-600' : 'text-green-600'}`}>
                {call.type}
              </span> */}
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {new Date(call.start_time).toLocaleString()} • Duration: {call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : 'N/A'}
            </p>
            {call.summary && (
              <div className="bg-blue-50 rounded p-2 text-sm text-blue-900 flex items-start space-x-2">
                <Bot className="w-4 h-4 mt-0.5" />
                <span>{call.summary}</span>
              </div>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </button>
  );
}

function APICallItem({ call, onClick }: { call: UserCall; onClick?: () => void }) {
  // lightweight rendering for API `UserCall` shape; clickable to open details
  return (
    <button onClick={onClick} className="w-full text-left">
      <div className="w-full bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4 flex-1">
            <div className={`p-3 rounded-full bg-green-100`}>
              <Phone className={`w-5 h-5 text-green-600`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-semibold text-gray-900">Call</span>
                <span className="text-xs font-medium text-gray-600">{call.user_name || call.user_phone}</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">{new Date(call.start_time).toLocaleString()}</p>
              {call.summary && (
                <div className="bg-blue-50 rounded p-2 text-sm text-blue-900">
                  <span>{call.summary}</span>
                </div>
              )}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </div>
      </div>
    </button>
  );
}

function MessageItem({ message, onClick }: { message: OmnichannelMessage; onClick: () => void }) {
  const getChannelIcon = () => {
    switch (message.channel) {
      case 'sms':
        return <MessageSquare className="w-5 h-5 text-purple-600" />;
      case 'email':
        return <Mail className="w-5 h-5 text-blue-600" />;
      default:
        return <Phone className="w-5 h-5 text-gray-600" />;
    }
  };

  const getChannelColor = () => {
    switch (message.channel) {
      case 'sms':
        return 'bg-purple-100';
      case 'email':
        return 'bg-blue-100';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <button
      onClick={onClick}
      className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-green-500 hover:shadow-md transition-all text-left"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className={`p-3 rounded-full ${getChannelColor()}`}>
            {getChannelIcon()}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className="font-semibold text-gray-900 uppercase text-sm">{message.channel}</span>
              <span className={`text-xs font-medium ${
                message.direction === 'inbound' ? 'text-blue-600' : 'text-green-600'
              }`}>
                {message.direction}
              </span>
              {message.aiContext && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                  AI-Assisted
                </span>
              )}
            </div>
            {message.subject && (
              <p className="font-medium text-gray-900 mb-1">{message.subject}</p>
            )}
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{message.content}</p>
            {/* <p className="text-xs text-gray-500">
              {new Date(message.timestamp).toLocaleString()} • {message.status}
            </p> */}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </div>
    </button>
  );
}

function CallDetailModal({ 
  call, 
  onClose, 
  onDownload,
  conversationDetails, 
  isLoading,
}: { 
  call: UserCall; 
  onClose: () => void; 
  onDownload: () => void; 
  conversationDetails?: ConversationDetails;
  isLoading?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Call Details</h3>
            <p className="text-sm text-gray-600">{new Date(call.start_time).toLocaleString()}</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={onDownload}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              title="Download Transcript"
            >
              <Download className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            {/* <div>
              <label className="text-sm font-medium text-gray-700">Type</label>
              <p className="text-gray-900 capitalize">{call.type}</p>
            </div> */}
            {/* <div>
              <label className="text-sm font-medium text-gray-700">Purpose</label>
              <p className="text-gray-900 capitalize">{call.purpose}</p>
            </div> */}
            <div>
              <label className="text-sm font-medium text-gray-700">Duration</label>
              <p className="text-gray-900">
                {call.duration_seconds ? `${call.duration_seconds}` : 'N/A'}
              </p>
            </div>
            {/* <div>
              <label className="text-sm font-medium text-gray-700">Urgency</label>
              <p className="text-gray-900 capitalize">{call.urgencyLevel}</p>
            </div> */}
          </div>

          {call.summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                <Bot className="w-5 h-5 mr-2" />
                AI Summary
              </h4>
              <p className="text-blue-800">{call.summary}</p>
            </div>
          )}

          <div>
            <h4 className="font-semibold text-gray-900 mb-4">Conversation Transcript</h4>
            <div className="space-y-3">
              {isLoading ? (
                <p className="text-gray-600">Loading conversation...</p>
              ) : (
                conversationDetails && conversationDetails.conversation.map((message, index) => (
                  <div
                    key={message.id || index}
                    className={`flex ${message.speaker === 'user' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-4 ${
                        message.speaker === 'user'
                          ? 'bg-gray-100 text-gray-900'
                          : 'bg-green-600 text-white'
                      }`}
                    >
                      <p className="text-sm font-medium mb-1">
                        {message.speaker === 'user' ? 'User' : 'assistant'}
                      </p>
                      <p className="text-sm">{message.text}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageDetailModal({ message, onClose }: { message: OmnichannelMessage; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900 uppercase">{message.channel} Message</h3>
            <p className="text-sm text-gray-600">{new Date(message.timestamp).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {message.subject && (
            <div>
              <label className="text-sm font-medium text-gray-700">Subject</label>
              <p className="text-gray-900 font-semibold">{message.subject}</p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">Direction</label>
            <p className={`text-gray-900 capitalize ${
              message.direction === 'inbound' ? 'text-blue-600' : 'text-green-600'
            }`}>
              {message.direction}
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Content</label>
            <div className="bg-gray-50 rounded-lg p-4 mt-2">
              <p className="text-gray-900 whitespace-pre-wrap">{message.content}</p>
            </div>
          </div>

          {message.aiContext && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                <Bot className="w-5 h-5 mr-2" />
                AI Context
              </h4>
              <p className="text-sm text-blue-800">
                Confidence: {Math.round(message.aiContext.confidence * 100)}%
              </p>
              {message.aiContext.clinicalDataUsed.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-blue-700 font-medium">Clinical Data Used:</p>
                  <ul className="list-disc list-inside text-xs text-blue-700 mt-1">
                    {message.aiContext.clinicalDataUsed.map((data, index) => (
                      <li key={index}>{data}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* <div>
            <label className="text-sm font-medium text-gray-700">Status</label>
            <p className="text-gray-900 capitalize">{message.status}</p>
          </div> */}
        </div>
      </div>
    </div>
  );
}
